/**
 * ReconciliationStats.js
 * Modern reconciliation dashboard statistics with elegant card design
 */

import React from 'react';
import {
  Box,
  Typography,
  Grid,
  LinearProgress,
  Chip,
  Stack,
  Avatar,
  Skeleton,
  Card,
  CardContent,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Description as DocumentIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { createTheme } from '@mui/material/styles';

// Import the theme directly
const defaultTheme = createTheme({
  palette: {
    primary: { main: '#2563eb', light: '#60a5fa', dark: '#1d4ed8' },
    secondary: { main: '#7c3aed', light: '#a78bfa', dark: '#5b21b6' },
    success: { main: '#059669', light: '#34d399', dark: '#047857' },
    error: { main: '#dc2626', light: '#f87171', dark: '#b91c1c' },
    warning: { main: '#d97706', light: '#fbbf24', dark: '#b45309' },
    info: { main: '#0891b2', light: '#22d3ee', dark: '#0e7490' },
    background: { default: '#fafafa', paper: '#ffffff' },
    text: { primary: '#111827', secondary: '#6b7280' },
  },
});

const ReconciliationStats = ({ stats, isLoading }) => {
  const theme = useTheme() || defaultTheme;
  if (isLoading) {
    return (
      <Grid container spacing={1}>
        {[1, 2, 3, 4].map((index) => (
          <Grid item xs={6} md={3} key={index}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Skeleton variant="circular" width={32} height={32} sx={{ mx: 'auto', mb: 1 }} />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="60%" />
            </Box>
          </Grid>
        ))}
      </Grid>
    );
  }

  const reconciliationProgress =
    stats.totalTransactions > 0
      ? (stats.reconciledTransactions / stats.totalTransactions) * 100
      : 0;

  const statementProgress =
    stats.totalStatements > 0 ? (stats.fullyReconciledStatements / stats.totalStatements) * 100 : 0;

  const statsItems = [
    {
      title: 'Total',
      value: stats.totalTransactions || 0,
      icon: <DocumentIcon fontSize="small" />,
      color: 'primary',
    },
    {
      title: 'Reconciliadas',
      value: stats.reconciledTransactions || 0,
      subtitle: `${reconciliationProgress.toFixed(0)}%`,
      icon: <CheckCircleIcon fontSize="small" />,
      color: 'success',
      progress: reconciliationProgress,
    },
    {
      title: 'Pendentes',
      value: stats.unreconciledTransactions || 0,
      icon: <ScheduleIcon fontSize="small" />,
      color: 'warning',
    },
    {
      title: 'Extratos OK',
      value: stats.fullyReconciledStatements || 0,
      subtitle: `${statementProgress.toFixed(0)}%`,
      icon: <TrendingUpIcon fontSize="small" />,
      color: 'info',
      progress: statementProgress,
    },
  ];

  const renderStatCard = (item, index, theme) => {
    return (
      <Grid item xs={6} md={3} key={index}>
        <Card
          elevation={0}
          sx={{
            height: '100%',
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.background.paper,
              0.9
            )} 0%, ${alpha(theme.palette.background.default, 0.6)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: 3,
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 40px ${alpha(
                item.color === 'primary'
                  ? theme.palette.primary.main
                  : item.color === 'success'
                  ? theme.palette.success.main
                  : item.color === 'warning'
                  ? theme.palette.warning.main
                  : item.color === 'info'
                  ? theme.palette.info.main
                  : theme.palette.grey[500],
                0.2
              )}`,
              borderColor: alpha(
                item.color === 'primary'
                  ? theme.palette.primary.main
                  : item.color === 'success'
                  ? theme.palette.success.main
                  : item.color === 'warning'
                  ? theme.palette.warning.main
                  : item.color === 'info'
                  ? theme.palette.info.main
                  : theme.palette.grey[500],
                0.3
              ),
            },
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center', height: '100%' }}>
            <Stack spacing={2} alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
              {/* Icon with Gradient Background */}
              <Box
                sx={{
                  position: 'relative',
                  p: 1.5,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette[item.color].main} 0%, ${
                    theme.palette[item.color].dark
                  } 100%)`,
                  boxShadow: `0 4px 20px ${alpha(theme.palette[item.color].main, 0.3)}`,
                }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: 'transparent',
                    color: 'white',
                  }}
                >
                  {item.icon}
                </Avatar>

                {/* Decorative Ring */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    right: -2,
                    bottom: -2,
                    borderRadius: '50%',
                    border: `2px solid ${alpha(theme.palette[item.color].main, 0.2)}`,
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': { transform: 'scale(1)', opacity: 1 },
                      '100%': { transform: 'scale(1.1)', opacity: 0 },
                    },
                  }}
                />
              </Box>

              {/* Value */}
              <Box>
                <Typography
                  variant="h4"
                  fontWeight="800"
                  sx={{
                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                    lineHeight: 1.2,
                    color: 'text.primary',
                    mb: 0.5,
                  }}
                >
                  {item.value.toLocaleString('pt-BR')}
                </Typography>

                {/* Title */}
                <Typography
                  variant="body2"
                  fontWeight="600"
                  sx={{
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem',
                  }}
                >
                  {item.title}
                </Typography>
              </Box>

              {/* Subtitle and Progress */}
              {(item.subtitle || item.progress !== undefined) && (
                <Box sx={{ width: '100%' }}>
                  {item.subtitle && (
                    <Chip
                      label={item.subtitle}
                      size="small"
                      sx={{
                        mb: item.progress !== undefined ? 1 : 0,
                        backgroundColor: alpha(theme.palette[item.color].main, 0.1),
                        color: theme.palette[item.color].main,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 20,
                      }}
                    />
                  )}

                  {item.progress !== undefined && (
                    <LinearProgress
                      variant="determinate"
                      value={item.progress}
                      sx={{
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette[item.color].main, 0.1),
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: theme.palette[item.color].main,
                          borderRadius: 2,
                        },
                      }}
                    />
                  )}
                </Box>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box>
      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {statsItems.map((item, index) => renderStatCard(item, index, theme))}
      </Grid>
    </Box>
  );
};

export default ReconciliationStats;
